import React from "react";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";

import Loading from "modules/common/components/loading";
import ManagementEmptyResults from "modules/common/components/managementEmptyResults";
import Paging from "modules/common/components/paging";
import moment from "moment";
import { ROWS_PER_PAGE_COOKIE_NAMES } from "utils/cookie";
import { ISMSFilters, ISMSListingPage, ISMSListItem, SMSSortStyle, SMSStatus } from "./models";
import SMSOptions from "./smsOptions";
import { dateOptions } from "utils/dateFormatting";

import "./styles/smsTable.sass";

interface IHeaderCell {
    id: string;
    label: string;
    ascValue?: SMSSortStyle;
    descValue?: SMSSortStyle;
    clickable?: boolean;
}

interface ISMSTableProps {
    page: ISMSListingPage;
    show: boolean;
    showIsLoading: boolean;
    hasFiltersApplied: boolean;
    filters: ISMSFilters;

    onPreviewSMS: (sms: ISMSListItem) => void;
    handleDraftSMSClick: (sms: ISMSListItem) => void;
    onChangeFilters: (newFilters: ISMSFilters) => void;
    onDeleteSMS: (sms: ISMSListItem) => void;
    onDuplicateSMS: (sms: ISMSListItem) => void;
    onUnsendSMS: (sms: ISMSListItem) => void;
    onChangePage: (page: number, rowsPerPage: number, filters: ISMSFilters) => void;
    onDownloadSMSActivity: (smsId: string) => void;
}

const SMSTable: React.FC<ISMSTableProps> = ({
    page,
    show,
    showIsLoading,
    hasFiltersApplied,
    filters,
    onPreviewSMS,
    handleDraftSMSClick,
    onChangeFilters,
    onDeleteSMS,
    onDuplicateSMS,
    onUnsendSMS,
    onChangePage,
    onDownloadSMSActivity
}) => {

    const getPublishedTimeTabLabel = () => {
        switch (page.id) {
            case SMSStatus.Draft:
                return "Last modified on";
            case SMSStatus.Scheduled:
                return "Send on";
            case SMSStatus.Sent:
                return "Sent on";
        } 
    }

    const formatTime = (time?: Date): string => time ? moment(time).format(dateOptions.basicWithHours) : "-";
    const formatTitle = (title?: string): string => title ? title : "No title";

    const onClickHeader = (descValue?: SMSSortStyle, ascValue?: SMSSortStyle) => {
        let updatedFilters = { ...filters };

        if (descValue === undefined || ascValue === undefined) {
            return;
        }

        if (filters.sortBy === descValue) {
            updatedFilters = { ...updatedFilters, sortBy: ascValue };
        } else {
            updatedFilters = { ...updatedFilters, sortBy: descValue };
        }

        onChangeFilters(updatedFilters);
    }
    
    const getHeader = () => {
        let header: IHeaderCell[] = [
            {
                id: "title",
                label: "Internal title",
                ascValue: SMSSortStyle.TitleAsc,
                descValue: SMSSortStyle.TitleDesc,
                clickable: true
            }
        ];

        header.push({ id: "options", label: "" });

        header.push({
            id: "modifiedOn",
            label: getPublishedTimeTabLabel(),
            ascValue: page.id === SMSStatus.Draft ? SMSSortStyle.ModifiedAtAsc : SMSSortStyle.SentTimeAsc,
            descValue: page.id === SMSStatus.Draft ? SMSSortStyle.ModifiedAtDesc : SMSSortStyle.SentTimeDesc,
            clickable: true
        });

        header.push({
            id: "author",
            label: page.id === SMSStatus.Draft ? "Last modified by" : "Published by",
            ascValue: page.id === SMSStatus.Draft ? SMSSortStyle.ModifiedByAsc : SMSSortStyle.SentByAsc,
            descValue: page.id === SMSStatus.Draft ? SMSSortStyle.ModifiedByDesc : SMSSortStyle.SentByDesc,
            clickable: true
        });

        header.push({
            id: "fromNumber",
            label: "From number",
            ascValue: SMSSortStyle.FromNumberAsc,
            descValue: SMSSortStyle.FromNumberDesc,
            clickable: true
        });

        return header;
    }

    if(!show) {
        return <></>;
    }
    else if (showIsLoading || !page.sms?.length) {
        if(showIsLoading) {
            return <div className="sms-table-loading-wheel"><Loading /></div>;
        }
        else {
            return <ManagementEmptyResults
                searchType={"Messages"} 
                hasFilters={hasFiltersApplied} 
            />
        }
    }
    else {
        return <>
            { hasFiltersApplied && <Typography variant="h2" className="filter-results">Results</Typography> }
            <TableContainer className="sms-table-container">
                <Table size="medium" id="sms-table">
                    <TableHead>
                        <TableRow>
                            {
                                getHeader().map((headerCell) => 
                                    <TableCell
                                        key={headerCell.id}
                                        align={headerCell.id === "title" ? "left" : "center"}
                                        padding={headerCell.id === "title" ? "none" : "normal"}
                                        onClick={() => onClickHeader(headerCell.descValue, headerCell.ascValue)}
                                    >
                                        <div
                                            className={`sms-table-cell
                                                ${(filters.sortBy === headerCell.ascValue || filters.sortBy === headerCell.descValue) && "sorted-by"}
                                                ${(!headerCell.clickable) && "unclickable"}
                                                ${(headerCell.id === "title") && "title-cell"}    
                                            `}
                                        >
                                            {headerCell.label}
                                            {filters.sortBy === headerCell.ascValue && <ArrowUpward htmlColor="#7a7a7a" className="cell-arrow-sort-styling" />}
                                            {filters.sortBy === headerCell.descValue && <ArrowDownward htmlColor="#7a7a7a" className="cell-arrow-sort-styling" />}
                                        </div>
                                    </TableCell>
                                )
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            page.sms.map(sms => 
                                <TableRow
                                    hover
                                    onClick={() => sms.status === "draft" ? handleDraftSMSClick(sms) : onPreviewSMS(sms)}
                                    tabIndex={-1}
                                    key={sms.id}
                                    className="sms-table-row"
                                >
                                    <TableCell component="th" scope="row" padding="none">
                                        <span className="sms-title-row-value">{formatTitle(sms.title)}</span>
                                    </TableCell>
                                    <TableCell>
                                        <SMSOptions 
                                            sms={sms}
                                            onDownloadSMSActivity={sms.status !== "draft" ? (sms) => onDownloadSMSActivity(sms.id) : undefined}
                                            onEditSMS={sms.status === "draft" ? handleDraftSMSClick : undefined}
                                            onDuplicateSMS={onDuplicateSMS}
                                            onPreviewSMS={onPreviewSMS}
                                            onDeleteSMS={sms.status === "draft" || sms.status === "scheduled" ? onDeleteSMS : undefined}
                                            onUnsendSMS={sms.status === "scheduled" ? onUnsendSMS : undefined}
                                        />
                                    </TableCell>
                                    <TableCell align="center">{formatTime(sms.status === "draft" ? sms.lastModification.modifiedTime : sms.sentTime)}</TableCell>
                                    <TableCell align="center">{(sms.status === "draft" ? sms.lastModification?.modifiedBy.name : sms.sentBy?.name) ?? ""}</TableCell>
                                    <TableCell align="center" className="sms-from-sender-table-cell">{sms.fromNumber}</TableCell>
                                </TableRow>
                            )
                        }
                    </TableBody>
                </Table>
            </TableContainer>
            <Paging
                currentPage={page.currentPage}
                items={page.sms}
                totalItems={page.totalSMS}
                totalPages={page.totalPages}
                onChangePage={(page, rowsPerPage) => onChangePage(page, rowsPerPage, filters)}
                rowsCookieName={ROWS_PER_PAGE_COOKIE_NAMES.SMS}
                defaultRowsPerPage={25}
                alwaysShow
            />
        </>;
    }
}

export default SMSTable;